import * as Sentry from "@sentry/vue";

const SENTRY_MAX_CONTEXT_STRING_LENGTH = 500;

const parseSampleRate = (value) => {
  const sampleRate = Number(value);

  if (Number.isNaN(sampleRate)) {
    return 0;
  }

  return Math.min(Math.max(sampleRate, 0), 1);
};

const hasSentryDsn = () => Boolean(process.env.VUE_APP_SENTRY_DSN);

const isSwishEvent = (event = {}) => {
  const tags = event.tags || {};

  return Boolean(
    tags.feature === "swish" ||
      tags.swish_event ||
      String(event.message || "").startsWith("swish.")
  );
};

const beforeSend = (event) => {
  if (!isSwishEvent(event)) {
    return null;
  }

  return event;
};

const sanitizeSentryValue = (value) => {
  if (value === undefined || typeof value === "function") {
    return undefined;
  }

  if (
    value === null ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }

  if (typeof value === "string") {
    return value.length > SENTRY_MAX_CONTEXT_STRING_LENGTH
      ? `${value.slice(0, SENTRY_MAX_CONTEXT_STRING_LENGTH)}...`
      : value;
  }

  if (value instanceof Error) {
    return {
      message: value.message,
      name: value.name,
    };
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => sanitizeSentryValue(item))
      .filter((item) => item !== undefined);
  }

  if (typeof value === "object") {
    return Object.entries(value).reduce((accumulator, [key, item]) => {
      const sanitizedValue = sanitizeSentryValue(item);

      if (sanitizedValue !== undefined) {
        accumulator[key] = sanitizedValue;
      }

      return accumulator;
    }, {});
  }

  return String(value);
};

const sanitizeSentryContext = (context = {}) =>
  sanitizeSentryValue(context) || {};

const applySwishScope = (
  scope,
  { context = {}, level = "info", tags = {} }
) => {
  const sanitizedContext = sanitizeSentryContext(context);

  scope.setLevel(level);
  scope.setTag("feature", "swish");

  Object.entries({
    order_id: sanitizedContext.orderId,
    ...tags,
  }).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      scope.setTag(key, String(value));
    }
  });

  scope.setContext("swish", sanitizedContext);
};

export const initSentry = (app, options = {}) => {
  const dsn = process.env.VUE_APP_SENTRY_DSN;

  if (!dsn) {
    return;
  }

  const tracesSampleRate = parseSampleRate(
    process.env.VUE_APP_SENTRY_TRACES_SAMPLE_RATE
  );
  const sentryOptions = {
    app,
    dsn,
    environment: process.env.VUE_APP_ENVIRONMENT || "development",
    beforeSend,
    sendDefaultPii: true,
  };

  if (tracesSampleRate > 0) {
    sentryOptions.tracesSampleRate = tracesSampleRate;
    sentryOptions.integrations = [
      Sentry.browserTracingIntegration(
        options.router ? { router: options.router } : undefined
      ),
    ];
  }

  Sentry.init(sentryOptions);
};

export const addSwishBreadcrumb = (message, data = {}, level = "info") => {
  if (!hasSentryDsn()) {
    return;
  }

  Sentry.addBreadcrumb({
    category: "swish",
    data: sanitizeSentryContext(data),
    level,
    message,
  });
};

export const captureSwishMessage = (
  message,
  { context = {}, level = "warning", tags = {} } = {}
) => {
  if (!hasSentryDsn()) {
    return;
  }

  Sentry.withScope((scope) => {
    applySwishScope(scope, {
      context,
      level,
      tags: {
        ...tags,
        swish_event: message,
      },
    });
    Sentry.captureMessage(message);
  });
};

export const captureSwishException = (
  error,
  message,
  { context = {}, level = "error", tags = {} } = {}
) => {
  if (!hasSentryDsn()) {
    return;
  }

  Sentry.withScope((scope) => {
    applySwishScope(scope, {
      context: {
        ...context,
        sentryMessage: message,
      },
      level,
      tags: {
        ...tags,
        swish_event: message,
      },
    });
    Sentry.captureException(
      error instanceof Error ? error : new Error(message)
    );
  });
};
